import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, FloppyDisk, ArrowCounterClockwise } from '@phosphor-icons/react';
import { invalidateWorkingHoursCache } from '../time-entries/AdminPunchModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_LABELS = [
  { idx: '0', label: 'Lundi' },
  { idx: '1', label: 'Mardi' },
  { idx: '2', label: 'Mercredi' },
  { idx: '3', label: 'Jeudi' },
  { idx: '4', label: 'Vendredi' },
  { idx: '5', label: 'Samedi' },
  { idx: '6', label: 'Dimanche' },
];

const computeHours = (start, end, brk) => {
  try {
    const [h1, m1] = (start || '00:00').split(':').map(Number);
    const [h2, m2] = (end || '00:00').split(':').map(Number);
    const diff = (h2 * 60 + m2) - (h1 * 60 + m1) - Number(brk || 0);
    return diff > 0 ? (diff / 60).toFixed(2) : '0.00';
  } catch { return '0.00'; }
};

export const WorkingHoursCard = ({ isAdmin }) => {
  const [hours, setHours] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/settings/working-hours`, { withCredentials: true });
      setHours(r.data?.hours || {});
    } catch {
      toast.error('Impossible de charger les horaires');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (idx, field, value) => {
    setHours(h => ({
      ...h,
      [idx]: {
        ...(h[idx] || {}),
        [field]: field === 'break' ? Number(value || 0) : value,
      },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings/working-hours`, { hours }, { withCredentials: true });
      invalidateWorkingHoursCache();
      toast.success('Horaires par defaut enregistres');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const resetDefaults = () => {
    if (!window.confirm('Restaurer les horaires BATI-BOYS par defaut (Lun-Jeu 8h-16h30, Ven 8h-12h, pause 30 min) ?')) return;
    setHours({
      '0': { start: '08:00', end: '16:30', break: 30 },
      '1': { start: '08:00', end: '16:30', break: 30 },
      '2': { start: '08:00', end: '16:30', break: 30 },
      '3': { start: '08:00', end: '16:30', break: 30 },
      '4': { start: '08:00', end: '12:00', break: 0 },
      '5': { start: '08:00', end: '17:00', break: 60 },
      '6': { start: '08:00', end: '17:00', break: 60 },
    });
  };

  if (!isAdmin) return null;
  if (loading) return null;

  return (
    <div className="card" data-testid="working-hours-card">
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Clock size={22} weight="bold" color="#1565C0" />
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>Horaires de travail par defaut</h3>
          <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#5E5F66' }}>
            Pre-remplis automatiquement les pointages selon le jour de la semaine.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={resetDefaults}
          data-testid="reset-working-hours-btn"
          title="Restaurer BATI-BOYS"
        >
          <ArrowCounterClockwise size={14} /> Defaut
        </button>
      </div>
      <div style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr 100px 70px', gap: 10, alignItems: 'center', fontSize: '0.74rem', color: '#5E5F66', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          <span>Jour</span>
          <span>Debut</span>
          <span>Fin</span>
          <span>Pause (min)</span>
          <span style={{ textAlign: 'right' }}>Total</span>
        </div>
        {DAY_LABELS.map(({ idx, label }) => {
          const v = hours[idx] || {};
          const total = computeHours(v.start, v.end, v.break);
          return (
            <div
              key={idx}
              data-testid={`working-hours-row-${idx}`}
              style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr 100px 70px', gap: 10, alignItems: 'center' }}
            >
              <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{label}</span>
              <input
                type="time"
                className="form-input"
                value={v.start || ''}
                data-testid={`wh-start-${idx}`}
                onChange={e => update(idx, 'start', e.target.value)}
              />
              <input
                type="time"
                className="form-input"
                value={v.end || ''}
                data-testid={`wh-end-${idx}`}
                onChange={e => update(idx, 'end', e.target.value)}
              />
              <input
                type="number"
                min="0"
                step="5"
                className="form-input"
                value={v.break ?? 0}
                data-testid={`wh-break-${idx}`}
                onChange={e => update(idx, 'break', e.target.value)}
              />
              <span style={{ textAlign: 'right', fontWeight: 700, color: Number(total) > 0 ? '#1F2024' : '#9E9EA7' }}>
                {total}h
              </span>
            </div>
          );
        })}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={save}
            disabled={saving}
            data-testid="save-working-hours-btn"
          >
            <FloppyDisk size={16} weight="bold" /> {saving ? 'Enregistrement...' : 'Enregistrer les horaires'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkingHoursCard;
